import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import type { NavGroup, NavItem } from "./nav-items";
import { cn } from "@/lib/cn";

const SEPARADOR = "|";
const idDoGrupo = (chave: string) => `grupo${SEPARADOR}${chave}`;
const idDoItem = (chave: string, href: string) => `item${SEPARADOR}${chave}${SEPARADOR}${href}`;

/**
 * Só concorre quem pode receber: grupo mira grupo, item mira item do mesmo grupo.
 *
 * Sem isso, arrastar o grupo "Laboratório" um pouco pra cima encontra primeiro a linha "Equipe",
 * que está dentro de outro grupo — o alvo é descartado e o arraste não faz nada. Restringir aqui,
 * e não só ao soltar, também faz a lista abrir espaço no lugar certo enquanto se arrasta.
 */
const mesmoNivel: CollisionDetection = (args) => {
  const ativo = String(args.active.id).split(SEPARADOR);
  const candidatos = args.droppableContainers.filter((c) => {
    const alvo = String(c.id).split(SEPARADOR);
    if (alvo[0] !== ativo[0]) return false;
    return ativo[0] !== "item" || alvo[1] === ativo[1];
  });
  return closestCenter({ ...args, droppableContainers: candidatos });
};

type Props = {
  grupos: NavGroup[];
  onGrupos: (chaves: string[]) => void;
  onItens: (chaveDoGrupo: string, hrefs: string[]) => void;
};

/**
 * O menu no modo Reorganizar: as mesmas linhas, sem navegar, cada uma com uma alça.
 *
 * Grupo sem rótulo (Início, Apps Pro) tem um item só — a linha do item É o grupo e arrasta o grupo
 * inteiro. Uma alça de grupo em cima de uma alça de item, ambas movendo a mesma coisa, seria só
 * confusão. Item nunca cruza de grupo: "Agenda" dentro de "Laboratório" não quer dizer nada.
 */
export function MenuReordenavel({ grupos, onGrupos, onItens }: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const aoSoltar = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const ativo = String(e.active.id).split(SEPARADOR);
    const alvo = String(e.over.id).split(SEPARADOR);
    if (ativo[0] !== alvo[0]) return;

    if (ativo[0] === "grupo") {
      const chaves = grupos.map((g) => g.key);
      const de = chaves.indexOf(ativo[1]);
      const para = chaves.indexOf(alvo[1]);
      if (de === -1 || para === -1) return;
      onGrupos(arrayMove(chaves, de, para));
      return;
    }

    if (ativo[1] !== alvo[1]) return;
    const grupo = grupos.find((g) => g.key === ativo[1]);
    if (!grupo) return;
    const hrefs = grupo.items.map((i) => i.href);
    const de = hrefs.indexOf(ativo[2]);
    const para = hrefs.indexOf(alvo[2]);
    if (de === -1 || para === -1) return;
    onItens(grupo.key, arrayMove(hrefs, de, para));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={mesmoNivel}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={aoSoltar}
    >
      <SortableContext items={grupos.map((g) => idDoGrupo(g.key))} strategy={verticalListSortingStrategy}>
        {grupos.map((grupo) => (
          <GrupoArrastavel key={grupo.key} grupo={grupo} />
        ))}
      </SortableContext>
    </DndContext>
  );
}

function GrupoArrastavel({ grupo }: { grupo: NavGroup }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: idDoGrupo(grupo.key),
  });
  const estilo = { transform: CSS.Transform.toString(transform), transition };

  // Grupo de um item só e sem rótulo: a linha do item arrasta o grupo.
  if (!grupo.label && grupo.items.length <= 1) {
    const item = grupo.items[0];
    if (!item) return null;
    return (
      <div ref={setNodeRef} style={estilo} className={cn(isDragging && "z-10 opacity-80")}>
        <LinhaArrastavel item={item} alca={{ ...attributes, ...listeners }} arrastando={isDragging} />
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={estilo}
      className={cn(
        "rounded-lg border border-dashed border-zinc-200 p-1 dark:border-border",
        isDragging && "z-10 border-primary/50 opacity-80",
      )}
    >
      <div className="flex items-center gap-1 px-1 pb-1">
        <Alca {...attributes} {...listeners} rotulo={`Mover o grupo ${grupo.label ?? grupo.items.map((i) => i.label).join(" e ")}`} />
        <p className="truncate text-[9.5px] font-semibold uppercase tracking-widest text-muted-foreground">
          {grupo.label}
        </p>
      </div>
      <SortableContext
        items={grupo.items.map((i) => idDoItem(grupo.key, i.href))}
        strategy={verticalListSortingStrategy}
      >
        {grupo.items.map((item) => (
          <ItemArrastavel key={item.href} chaveDoGrupo={grupo.key} item={item} />
        ))}
      </SortableContext>
    </div>
  );
}

function ItemArrastavel({ chaveDoGrupo, item }: { chaveDoGrupo: string; item: NavItem }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: idDoItem(chaveDoGrupo, item.href),
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(isDragging && "z-10 opacity-80")}
    >
      <LinhaArrastavel item={item} alca={{ ...attributes, ...listeners }} arrastando={isDragging} />
    </div>
  );
}

function LinhaArrastavel({
  item,
  alca,
  arrastando,
}: {
  item: NavItem;
  alca: Record<string, unknown>;
  arrastando: boolean;
}) {
  const Icon = item.icon;
  return (
    <div
      className={cn(
        "flex items-center gap-1.5 rounded-md px-1 py-1.5 text-[13px] leading-tight text-zinc-600 dark:text-muted-foreground",
        arrastando ? "bg-brand-50 dark:bg-primary/10" : "hover:bg-zinc-50 dark:hover:bg-muted",
      )}
    >
      <Alca {...alca} rotulo={`Mover ${item.label}`} />
      <Icon className="h-4 w-4 shrink-0 text-zinc-400 dark:text-muted-foreground/70" />
      <span className="truncate">{item.label}</span>
    </div>
  );
}

function Alca({ rotulo, ...props }: { rotulo: string } & Record<string, unknown>) {
  return (
    <button
      type="button"
      aria-label={rotulo}
      title={rotulo}
      className="flex h-10 w-9 shrink-0 cursor-grab touch-none items-center justify-center rounded text-zinc-400 md:h-6 md:w-5 hover:text-zinc-600 active:cursor-grabbing dark:text-muted-foreground/70 dark:hover:text-foreground"
      {...props}
    >
      <GripVertical className="h-3.5 w-3.5" />
    </button>
  );
}
